import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { format, subMonths, startOfMonth, endOfMonth } from "date-fns";
import { calculateTrend, detectAnomalies, projectFuture } from "@/lib/report-analytics";

export interface TrendDataPoint {
  period: string;
  value: number;
  trend?: 'up' | 'down' | 'stable';
  anomaly?: boolean;
}

export interface TrendAnalysis {
  historical: TrendDataPoint[];
  trendLine: TrendDataPoint[];
  projection: TrendDataPoint[];
  summary: {
    trend: 'growing' | 'declining' | 'stable';
    averageChange: number;
    forecast: number;
  };
}

export function useTrendAnalysis(
  companyId: string,
  metric: 'cost' | 'distance' | 'efficiency' | 'alerts',
  months: number = 12
) {
  return useQuery({
    queryKey: ['trend-analysis', companyId, metric, months],
    queryFn: async () => {
      const historical: TrendDataPoint[] = [];
      
      // Buscar dados históricos mês a mês
      for (let i = months - 1; i >= 0; i--) {
        const date = subMonths(new Date(), i);
        const from = startOfMonth(date);
        const to = endOfMonth(date);
        
        const value = await fetchMetricForPeriod(companyId, metric, from, to);
        historical.push({
          period: format(from, 'MMM/yy'),
          value
        });
      }

      // Detectar anomalias
      const values = historical.map(h => h.value);
      const anomalies = detectAnomalies(values, 2);
      historical.forEach((h, i) => {
        h.anomaly = anomalies[i];
      });

      // Calcular linha de tendência
      const trendData = historical.map((h, i) => ({ x: i, y: h.value }));
      const trend = calculateTrend(trendData);
      
      const trendLine: TrendDataPoint[] = historical.map((h, i) => ({
        period: h.period,
        value: trend.points[i].y
      }));

      // Calcular tendência (up/down/stable)
      historical.forEach((h, i) => {
        if (i > 0) {
          const change = ((h.value - historical[i - 1].value) / historical[i - 1].value) * 100;
          h.trend = change > 5 ? 'up' : change < -5 ? 'down' : 'stable';
        }
      });

      // Projetar 3 meses futuros
      const futureValues = projectFuture(trend, 3, historical.length - 1);
      const projection: TrendDataPoint[] = futureValues.map((value, i) => {
        const futureDate = subMonths(new Date(), -1 * (i + 1));
        return {
          period: format(futureDate, 'MMM/yy'),
          value
        };
      });

      // Calcular média de mudança mensal
      let totalChange = 0;
      for (let i = 1; i < historical.length; i++) {
        totalChange += ((historical[i].value - historical[i - 1].value) / historical[i - 1].value) * 100;
      }
      const averageChange = totalChange / (historical.length - 1);

      // Determinar tendência geral
      const overallTrend: 'growing' | 'declining' | 'stable' = 
        trend.slope > 0.1 ? 'growing' : 
        trend.slope < -0.1 ? 'declining' : 
        'stable';

      return {
        historical,
        trendLine,
        projection,
        summary: {
          trend: overallTrend,
          averageChange,
          forecast: projection[0]?.value || 0
        }
      } as TrendAnalysis;
    },
    enabled: !!companyId
  });
}

async function fetchMetricForPeriod(
  companyId: string,
  metric: 'cost' | 'distance' | 'efficiency' | 'alerts',
  from: Date,
  to: Date
): Promise<number> {
  const fromStr = format(from, 'yyyy-MM-dd');
  const toStr = format(to, 'yyyy-MM-dd');

  switch (metric) {
    case 'cost': {
      const [fuel, maintenance] = await Promise.all([
        supabase.from('fleet_fuel_logs').select('liters, price_unit').eq('company_id', companyId).gte('occurred_at', fromStr).lte('occurred_at', toStr),
        supabase.from('fleet_work_orders').select('id').eq('company_id', companyId).gte('opened_at', fromStr).lte('opened_at', toStr)
      ]);
      const fuelCost = fuel.data?.reduce((sum, f) => sum + ((f.liters || 0) * (f.price_unit || 0)), 0) || 0;
      const maintenanceCost = (maintenance.data?.length || 0) * 500;
      return fuelCost + maintenanceCost;
    }
    
    case 'distance': {
      const { data } = await supabase
        .from('fleet_trips')
        .select('distance_actual_km')
        .eq('company_id', companyId)
        .gte('start_date', fromStr)
        .lte('start_date', toStr);
      return data?.reduce((sum, t) => sum + (t.distance_actual_km || 0), 0) || 0;
    }
    
    case 'efficiency': {
      const [trips, fuel] = await Promise.all([
        supabase.from('fleet_trips').select('distance_actual_km').eq('company_id', companyId).gte('start_date', fromStr).lte('start_date', toStr),
        supabase.from('fleet_fuel_logs').select('liters').eq('company_id', companyId).gte('occurred_at', fromStr).lte('occurred_at', toStr)
      ]);
      const totalDistance = trips.data?.reduce((sum, t) => sum + (t.distance_actual_km || 0), 0) || 0;
      const totalLiters = fuel.data?.reduce((sum, f) => sum + (f.liters || 0), 0) || 0;
      return totalLiters > 0 ? totalDistance / totalLiters : 0;
    }
    
    case 'alerts': {
      const { data } = await supabase
        .from('fleet_alerts')
        .select('id')
        .eq('company_id', companyId)
        .gte('created_at', fromStr)
        .lte('created_at', toStr);
      return data?.length || 0;
    }
    
    default:
      return 0;
  }
}
